/**
 * Page Flip Utility
 * Wrapper for St.PageFlip library to handle story page animations
 */

// St.PageFlip is loaded as a global via a script tag, not imported — see
// loadPageFlipLibrary() in views/StoryReader.js. It is served from the public
// root as /page-flip.browser.js (the file lives in child-app/pwa/).

import { convertLineBreaks } from './textProcessing.js';
import { detectDeviceAndOrientation, supportsContainerQueries } from './deviceDetection.js';
import {
  getTextSizeValue,
  calculateConstrainedPageFlipDimensions,
  getPageFlipMode,
} from '../config/deviceBreakpoints.js';
import { t } from '../i18n/config.js';
import { isFeatureEnabled } from '../config/featureFlags.js';

/**
 * Detect if the browser is Firefox.
 * Firefox has rendering issues with StPageFlip's soft draw mode,
 * so we force hard draw for all pages on Firefox.
 */
const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);

/**
 * Initialize page flip for a story
 * @param {HTMLElement} container - Container element for the page flip
 * @param {Array} pages - Array of page data objects
 * @param {Object} callbacks - Callbacks for page flip events
 * @returns {Object} PageFlip instance
 */
export function initializePageFlip(container, pages, options = {}) {
  if (typeof St === 'undefined' || !St.PageFlip) {
    console.error('St.PageFlip library not loaded');
    return null;
  }

  try {
    // Get the actual container dimensions from the parent story-content element
    const storyContent = container.parentElement;

    // Force layout calculation by accessing offsetHeight first
    // This ensures clientWidth/clientHeight are accurate for absolutely positioned elements
    if (storyContent) {
      // Force reflow by accessing offsetHeight
      storyContent.offsetHeight; // eslint-disable-line no-unused-expressions
    }

    // Calculate available space from the actual container dimensions
    // .story-content fills the space between navigation (72px) and audio controls (60px)
    // using flexbox layout, so we use the actual rendered dimensions
    const containerWidth = storyContent ? storyContent.clientWidth : window.innerWidth;
    const containerHeight = storyContent ? storyContent.clientHeight : window.innerHeight;

    console.log('📏 Container dimensions:', {
      storyContentClientWidth: storyContent?.clientWidth,
      storyContentClientHeight: storyContent?.clientHeight,
      storyContentOffsetHeight: storyContent?.offsetHeight,
      storyContentBoundingHeight: storyContent?.getBoundingClientRect().height,
      containerWidth,
      containerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });

    // Use all available space to maximize book size
    const availableWidth = containerWidth;
    const availableHeight = containerHeight;

    // Determine orientation and device
    const isPortrait = options.forcePortrait || window.innerHeight > window.innerWidth;
    const deviceInfo = detectDeviceAndOrientation();
    const orientation = deviceInfo.orientation;

    // Get the PageFlip mode for this device and orientation
    // Force single-page mode when forcePortrait is set (e.g., recording mode)
    const pageFlipMode = options.forcePortrait
      ? 'single'
      : getPageFlipMode(deviceInfo.deviceId, orientation) || (isPortrait ? 'single' : 'double');

    // Calculate constrained dimensions that maintain proper aspect ratio
    // This prevents the book from becoming too wide and pages from becoming landscape-shaped
    const dimensions = calculateConstrainedPageFlipDimensions(
      availableWidth,
      availableHeight,
      pageFlipMode
    );

    console.log('📖 PageFlip configuration:', {
      containerWidth,
      containerHeight,
      availableWidth,
      availableHeight,
      isPortrait,
      deviceId: deviceInfo.deviceId,
      pageFlipMode,
      constrainedDimensions: dimensions,
      aspectRatio: pageFlipMode === 'double' ? '2:1.4' : '1:1.4',
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });

    // Size the container to match the book dimensions so the parent
    // flexbox (.story-content with align-items:center / justify-content:center)
    // can center it properly. StPageFlip reads offsetWidth/offsetHeight from
    // this container for its boundsRect calculation.
    // In double mode: container = 2 × pageWidth (spread). In single mode: container = pageWidth.
    const bookWidth = pageFlipMode === 'single' ? dimensions.pageWidth : dimensions.pageWidth * 2;
    const bookHeight = dimensions.pageHeight;
    container.style.width = bookWidth + 'px';
    container.style.minWidth = bookWidth + 'px';
    container.style.maxWidth = bookWidth + 'px';
    container.style.height = bookHeight + 'px';
    container.style.maxHeight = '100%';
    container.style.overflow = 'hidden';
    container.style.position = 'relative';

    // Force reflow so StPageFlip reads correct container dimensions
    container.offsetHeight; // eslint-disable-line no-unused-expressions

    console.log('📐 Container sized:', {
      bookWidth,
      bookHeight,
      pageFlipMode,
      forcePortrait: !!options.forcePortrait,
      containerOffsetWidth: container.offsetWidth,
      containerOffsetHeight: container.offsetHeight,
    });

    // Initialize PageFlip with constrained dimensions
    const pageFlip = new St.PageFlip(container, {
      // Page dimensions (constrained to maintain aspect ratio)
      width: dimensions.pageWidth,
      height: dimensions.pageHeight,

      // In single-page mode use 'stretch' so StPageFlip adapts to the narrow container
      // In double mode use 'fixed' for precise control
      size: pageFlipMode === 'single' ? 'stretch' : 'fixed',

      // For stretch mode, set min/max to constrain the page
      minWidth: pageFlipMode === 'single' ? dimensions.pageWidth : undefined,
      maxWidth: pageFlipMode === 'single' ? dimensions.pageWidth : undefined,
      minHeight: pageFlipMode === 'single' ? dimensions.pageHeight : undefined,
      maxHeight: pageFlipMode === 'single' ? dimensions.pageHeight : undefined,

      // Animation settings
      flippingTime: 600,
      drawShadow: true,

      // Shadow opacity (0-1, lower = lighter shadows)
      shadowOpacity: 0.3,

      // Portrait mode settings - force portrait in recording mode for single-page view
      usePortrait: options.forcePortrait || isPortrait,
      mobileScrollSupport: true,

      // Interaction settings
      showCover: true,
      startPage: 0,
      maxAngle: 0,
      startZIndex: 10,
    });

    // After initialization, ensure overflow is hidden on StPageFlip elements
    // but do NOT force width/height to 100% — that breaks boundsRect calculations
    setTimeout(() => {
      const stfParent = container.querySelector('.stf__parent');

      if (stfParent) {
        stfParent.style.overflow = 'hidden';
      }
    }, 100);

    // Event listeners
    if (options.onFlip) {
      pageFlip.on('flip', (e) => {
        options.onFlip(e.data);
      });
    }

    if (options.onChangeState) {
      pageFlip.on('changeState', (e) => {
        options.onChangeState(e.data);
      });
    }

    if (options.onChangeOrientation) {
      pageFlip.on('changeOrientation', (e) => {
        console.log('📐 PageFlip orientation:', e.data);
        options.onChangeOrientation(e.data);
      });
    }

    console.log('✅ PageFlip initialized successfully with aspect ratio constraints');
    return pageFlip;
  } catch (error) {
    console.error('❌ Failed to initialize PageFlip:', error);
    return null;
  }
}

/**
 * Create HTML pages for the page flip
 * Cover and Index pages are prepended before story pages inside the flipbook.
 * @param {Array} pages - Array of page data objects
 * @param {string} storyTitle - Title of the story
 * @param {string} coverImageUrl - Optional cover image URL
 * @returns {Array} Array of HTML elements
 */
export function createPageElements(
  pages,
  storyTitle,
  coverImageUrl = null,
  audioMenuContext = null
) {
  const pageElements = [];

  const totalStoryPages = pages.length;

  // 1. Cover page (first page in flipbook)
  pageElements.push(createCoverPageElement(coverImageUrl, storyTitle));

  // 2. Audio Mode Menu page (second page in flipbook)
  pageElements.push(createIndexPageElement(audioMenuContext));

  // 3. Story pages
  pages.forEach((page, index) => {
    const currentPageNumber = index + 1;
    const pageElement = createPageElement(page, currentPageNumber, totalStoryPages);
    pageElements.push(pageElement);
  });

  // 3.5. Padding page — if total page count (cover + index + story pages + back cover)
  //      is odd, StPageFlip's close animation breaks. Insert an extra page
  //      using the cover image to make the count even.
  const totalWithoutPadding = 2 + pages.length + 1; // cover + index + stories + back cover
  if (totalWithoutPadding % 2 !== 0) {
    pageElements.push(createBackCoverPageElement(coverImageUrl, storyTitle));
  }

  // 4. Back cover page — mirrors the front cover so the last story page
  //    always has a partner in double-page (landscape) spread mode.
  pageElements.push(createBackCoverPageElement(coverImageUrl, storyTitle));

  return pageElements;
}

/**
 * Number of non-story pages prepended before story content (cover + index)
 */
export const PREPENDED_PAGE_COUNT = 2;

/**
 * Create the cover page element for StPageFlip
 * @param {string} coverImageUrl - Cover image URL
 * @param {string} storyTitle - Story title (fallback if no image)
 * @returns {HTMLElement} Cover page element
 */
function createCoverPageElement(coverImageUrl, storyTitle) {
  const pageElement = document.createElement('div');
  pageElement.className = 'page page--cover';
  pageElement.dataset.density = 'hard';

  const pageContent = document.createElement('div');
  pageContent.className = 'page-content page-content--cover';

  if (coverImageUrl) {
    const img = document.createElement('img');
    img.src = coverImageUrl;
    img.alt = storyTitle || 'Book cover';
    img.className = 'cover-page-image';
    img.onerror = function () {
      console.error('Failed to load cover image:', this.src);
      this.style.display = 'none';
      // Show title as fallback
      const fallback = document.createElement('h1');
      fallback.className = 'cover-page-title';
      fallback.textContent = storyTitle || '';
      pageContent.appendChild(fallback);
    };
    pageContent.appendChild(img);
  } else {
    const title = document.createElement('h1');
    title.className = 'cover-page-title';
    title.textContent = storyTitle || '';
    pageContent.appendChild(title);
  }

  pageElement.appendChild(pageContent);
  return pageElement;
}

/**
 * Create the back cover page element for StPageFlip.
 * Mirrors the front cover so the last story page has a partner
 * in double-page (landscape) spread mode.
 * @param {string} coverImageUrl - Cover image URL (same as front cover)
 * @param {string} storyTitle - Story title (fallback if no image)
 * @returns {HTMLElement} Back cover page element
 */
function createBackCoverPageElement(coverImageUrl, storyTitle) {
  const pageElement = document.createElement('div');
  pageElement.className = 'page page--back-cover';
  pageElement.dataset.density = 'hard';

  const pageContent = document.createElement('div');
  pageContent.className = 'page-content page-content--cover';

  if (coverImageUrl) {
    const img = document.createElement('img');
    img.src = coverImageUrl;
    img.alt = storyTitle || 'Book cover';
    img.className = 'cover-page-image';
    img.onerror = function () {
      this.style.display = 'none';
      const fallback = document.createElement('h1');
      fallback.className = 'cover-page-title';
      fallback.textContent = storyTitle || '';
      pageContent.appendChild(fallback);
    };
    pageContent.appendChild(img);
  } else {
    const title = document.createElement('h1');
    title.className = 'cover-page-title';
    title.textContent = storyTitle || '';
    pageContent.appendChild(title);
  }

  pageElement.appendChild(pageContent);
  return pageElement;
}

/**
 * Create the Audio Mode Menu page — card-based menu for selecting audio mode.
 * Shows contextual options based on available audio and recordings.
 * @param {Object|null} context - Audio menu context
 * @param {boolean} context.hasPublisherAudio - Whether publisher narration exists
 * @param {string} context.childAvatarUrl - Child's avatar URL
 * @param {Array} context.adultRecordings - Array of {name, avatarUrl} for adult recordings
 * @returns {HTMLElement} Index page element with Audio Mode Menu
 */
function createIndexPageElement(context) {
  const pageElement = document.createElement('div');
  pageElement.className = 'page page--index';
  pageElement.dataset.density = isFirefox ? 'hard' : 'soft';

  const pageContent = document.createElement('div');
  pageContent.className = 'page-content page-content--index';

  const menuContainer = document.createElement('div');
  menuContainer.className = 'audio-mode-menu';

  // Build menu cards based on context
  const cards = buildAudioModeCards(context);

  // Separate adult cards from other cards for slider treatment
  const adultCards = cards.filter((c) => c.type === 'adult');
  const otherCards = cards.filter((c) => c.type !== 'adult');

  // Add non-adult cards that come before adults (publisher)
  const beforeAdult = otherCards.filter((c) => c.type === 'publisher');
  beforeAdult.forEach((card) => {
    menuContainer.appendChild(createAudioModeCard(card));
  });

  // If multiple adult recordings, wrap in a slider with left/right controls
  if (adultCards.length > 1) {
    const slider = document.createElement('div');
    slider.className = 'audio-mode-slider';

    const leftBtn = document.createElement('button');
    leftBtn.className = 'audio-mode-slider__btn audio-mode-slider__btn--left';
    leftBtn.innerHTML = '‹';
    leftBtn.setAttribute('aria-label', 'Previous');

    const track = document.createElement('div');
    track.className = 'audio-mode-slider__track';

    adultCards.forEach((card) => {
      const cardEl = createAudioModeCard(card);
      track.appendChild(cardEl);
    });

    const rightBtn = document.createElement('button');
    rightBtn.className = 'audio-mode-slider__btn audio-mode-slider__btn--right';
    rightBtn.innerHTML = '›';
    rightBtn.setAttribute('aria-label', 'Next');

    slider.appendChild(leftBtn);
    slider.appendChild(track);
    slider.appendChild(rightBtn);
    menuContainer.appendChild(slider);

    // Slider navigation logic
    let sliderIndex = 0;
    const updateSlider = () => {
      const cardEls = track.querySelectorAll('.audio-mode-card');
      cardEls.forEach((el, i) => {
        el.style.display = i === sliderIndex ? '' : 'none';
      });
      leftBtn.disabled = sliderIndex === 0;
      rightBtn.disabled = sliderIndex >= adultCards.length - 1;
    };
    leftBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (sliderIndex > 0) {
        sliderIndex--;
        updateSlider();
      }
    });
    rightBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (sliderIndex < adultCards.length - 1) {
        sliderIndex++;
        updateSlider();
      }
    });
    updateSlider();
  } else {
    // Single or no adult card — just append normally
    adultCards.forEach((card) => {
      menuContainer.appendChild(createAudioModeCard(card));
    });
  }

  // Add remaining cards (record, read)
  const afterAdult = otherCards.filter((c) => c.type !== 'publisher');
  afterAdult.forEach((card) => {
    menuContainer.appendChild(createAudioModeCard(card));
  });

  pageContent.appendChild(menuContainer);
  pageElement.appendChild(pageContent);

  // Start avatar wiggle animation once the element is in the DOM
  startAvatarWiggleWhenReady(pageElement);

  return pageElement;
}

/** Active wiggle interval ID — cleared on cleanup */
let avatarWiggleIntervalId = null;

/**
 * Watch for #mode-read to appear in the DOM, then start the wiggle loop.
 * Uses a MutationObserver so it works regardless of when StPageFlip renders the page.
 */
function startAvatarWiggleWhenReady(pageElement) {
  // Try immediately (element might already be in DOM)
  const existing = document.getElementById('mode-read');
  if (existing) {
    startAvatarWiggle(existing);
    return;
  }

  // Otherwise observe until it appears
  const observer = new MutationObserver(() => {
    const btn = document.getElementById('mode-read');
    if (btn) {
      observer.disconnect();
      startAvatarWiggle(btn);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Safety: stop observing after 10 seconds to avoid leaks
  setTimeout(() => observer.disconnect(), 10000);
}

/**
 * Infinite 3-second loop that flips the avatar image inside #mode-read
 * horizontally (scaleX) each cycle.
 */
function startAvatarWiggle(btn) {
  const avatar = btn.querySelector('.audio-mode-card__avatar');
  if (!avatar) return;

  if (avatarWiggleIntervalId) {
    clearInterval(avatarWiggleIntervalId);
  }

  // Instant flip — no transition, so the eyes appear to look left/right
  avatar.style.transition = 'none';
  let flipped = false;

  avatarWiggleIntervalId = setInterval(() => {
    flipped = !flipped;
    avatar.style.transform = flipped ? 'scaleX(-1)' : 'scaleX(1)';
  }, 3000);
}

/**
 * Build the list of audio mode cards based on available context.
 * Exported for testing — it is the single place that decides which audio
 * options a profile is offered.
 *
 * @param {Object} context
 * @param {boolean} context.hasPublisherAudio
 * @param {Array} context.adultRecordings
 * @param {string} context.profileType - 'child' or 'adult'
 * @param {string} [context.childAvatarUrl]
 * @returns {Array<Object>} Card descriptors
 */
export function buildAudioModeCards(context) {
  const cards = [];
  const ctx = context || {};

  // 1. Publisher narration card (only if publisher audio exists AND feature is enabled)
  if (ctx.hasPublisherAudio && isFeatureEnabled('publisherNarration')) {
    cards.push({
      id: 'mode-publisher',
      mode: 'listen',
      avatarUrl: '/svg/branding/KassieAvatarNoBorder.svg',
      label: t('child.audioMenu.listenToKassie') || 'Listen to Kassie',
      type: 'publisher',
    });
  }

  // 2. Adult narration cards.
  // These exist so children can listen to a grown-up reading. An adult profile
  // has no reason to be offered their own (or another adult's) narration, and
  // seeing "Luister na <my own name>" was reported as confusing (#145).
  // Adults review their own work with the play button in record mode instead.
  const isAdultViewer = ctx.profileType === 'adult';
  if (!isAdultViewer && ctx.adultRecordings && ctx.adultRecordings.length > 0) {
    ctx.adultRecordings.forEach((adult, index) => {
      let avatarUrl = adult.avatarUrl || '/svg/branding/KassieAvatarNoBorder.svg';
      // Only a bare filename gets the components prefix. An absolute path is
      // already resolved, and prefixing it produced
      // "/svg/components//svg/components/GrownUp-1.svg", which 404s and falls
      // through to the onerror handler — which is why this card showed the
      // generic grown-up icon while every other view showed the real avatar.
      if (avatarUrl && !avatarUrl.startsWith('http') && !avatarUrl.startsWith('/')) {
        avatarUrl = `/svg/components/${avatarUrl}`;
      }
      // First name only. displayName carries the full name, and a surname here
      // both overflows the card and reads oddly to a child.
      const firstName = String(adult.name || '').trim().split(/\s+/)[0] || adult.name;
      cards.push({
        id: `mode-adult-${index}`,
        mode: 'listen-adult',
        avatarUrl: avatarUrl,
        label: t('child.audioMenu.listenTo', { name: firstName }) || `Listen to ${firstName}`,
        type: 'adult',
        adultIndex: index,
        adultName: firstName,
      });
    });
  }

  // 3. Child/Adult recording card (always shown)
  const childAvatar = ctx.childAvatarUrl || '/svg/branding/KassieAvatarNoBorder.svg';
  const isAdultProfile = ctx.profileType === 'adult';
  const recordLabel = isAdultProfile
    ? t('child.audioMenu.recordForKids') || 'Record a story for the kids'
    : t('child.audioMenu.recordMyself') || 'I want to record and read';
  cards.push({
    id: 'mode-record',
    mode: 'record',
    avatarUrl: childAvatar,
    label: recordLabel,
    type: 'record',
  });

  // 3b. Carry on where they left off. Only for a child, and only when there is
  // a page to resume to — an adult already resumes automatically, and offering
  // this with nothing recorded would be a dead end.
  if (!isAdultProfile && typeof ctx.resumePage === 'number') {
    cards.push({
      id: 'mode-record-resume',
      mode: 'record-resume',
      avatarUrl: '/svg/components/Idea.svg',
      noBorder: true,
      label: t('child.audioMenu.rememberMyStory') || 'Remember my story',
      type: 'record',
    });
  }

  // 4. Read without audio card (always shown)
  cards.push({
    id: 'mode-read',
    mode: 'read',
    avatarUrl: '/svg/components/IsolationMode.svg',
    label: t('child.audioMenu.readOnly') || 'Read without sound',
    type: 'read',
  });

  return cards;
}

/**
 * Create a single audio mode card element
 */
function createAudioModeCard(card) {
  const btn = document.createElement('button');
  btn.className = 'audio-mode-card';

  // In adult recording mode, pre-select the record card
  const isAdultRecordMode = document.documentElement.dataset.adultRecordMode === 'true';
  if (isAdultRecordMode && card.type === 'record') {
    btn.classList.add('audio-mode-card--selected');
  }

  btn.id = card.id;
  btn.dataset.mode = card.mode;
  btn.dataset.type = card.type;
  if (card.adultIndex !== undefined) {
    btn.dataset.adultIndex = card.adultIndex;
    btn.dataset.adultName = card.adultName;
  }
  btn.setAttribute('aria-label', card.label);

  const avatar = document.createElement('img');
  avatar.src = card.avatarUrl;
  avatar.alt = '';
  avatar.className = 'audio-mode-card__avatar';
  if (card.noBorder) avatar.classList.add('audio-mode-card__avatar--no-border');
  // No inline object-fit. An inline style outranks every stylesheet rule, so
  // setting "cover" here silently defeated the "contain" on
  // --no-border and cropped the lightbulb to fill its box. The base class
  // already applies cover for the circular avatars.
  avatar.setAttribute('aria-hidden', 'true');
  avatar.setAttribute('referrerpolicy', 'no-referrer');
  avatar.onerror = function () {
    this.src = '/svg/components/GrownUp-1.svg';
  };

  const label = document.createElement('span');
  label.className = 'audio-mode-card__label';
  label.textContent = card.label;

  btn.appendChild(avatar);
  btn.appendChild(label);
  return btn;
}

/**
 * Create a single page element with layout support
 * @param {Object} page - Page data object
 * @param {number} currentPageNumber - Current page number (1-based)
 * @param {number} totalPages - Total number of pages
 * @returns {HTMLElement} Page element
 */
function createPageElement(page, currentPageNumber, totalPages) {
  const pageElement = document.createElement('div');
  pageElement.className = 'page';
  pageElement.dataset.density = isFirefox ? 'hard' : 'soft';

  const pageContent = document.createElement('div');
  pageContent.className = 'page-content';

  // Get layout configuration from page data (with defaults)
  const imageSize = page.imageSize || 'center';
  const textAlign = page.textAlign || 'left';
  const textPosition = page.textPosition || 'bottom';
  const textSize = page.textSize || 'medium';

  // Detect layout mode
  const isFlowMode = textPosition === 'above-image' || textPosition === 'below-image';

  console.log(`📄 Creating page ${currentPageNumber}:`, {
    imageSize,
    textAlign,
    textPosition,
    textSize,
    isFlowMode,
    hasImage: !!(page.imageUrl && page.imageUrl !== ''),
    hasText: !!page.text,
    imageUrl: page.imageUrl,
    text: page.text?.substring(0, 50) + '...',
  });

  // Add page header (optional)
  if (page.title) {
    const header = document.createElement('h2');
    header.className = 'page-header';
    header.textContent = page.title;
    pageContent.appendChild(header);
  }

  // Create page body container
  const pageBody = document.createElement('div');
  pageBody.className = isFlowMode ? 'page-body page-body--flow' : 'page-body';

  if (isFlowMode) {
    // Flow mode: use flexbox with order
    const textOrder = textPosition === 'above-image' ? 1 : 2;
    const imageOrder = textPosition === 'above-image' ? 2 : 1;

    // Create image (if available)
    if (page.imageUrl && page.imageUrl !== '') {
      const imageContainer = document.createElement('div');
      imageContainer.className = `page-image page-image--${imageSize}`;
      imageContainer.style.order = imageOrder;

      const img = document.createElement('img');
      img.src = page.imageUrl;
      img.alt = 'Story illustration';

      img.onerror = function () {
        console.error('Failed to load image:', this.src);
        this.style.display = 'none';
      };

      img.onload = function () {
        console.log('Image loaded successfully:', this.src);
      };

      imageContainer.appendChild(img);
      pageBody.appendChild(imageContainer);
    }

    // Create text (only if there's actual text content)
    if (page.text && page.text.trim() !== '') {
      const textDiv = document.createElement('div');
      textDiv.className = 'page-text page-text--flow';
      textDiv.style.textAlign = textAlign;
      textDiv.style.order = textOrder;
      textDiv.dataset.textSize = textSize;

      // Create text content wrapper
      const textContent = document.createElement('div');
      textContent.className = 'text-content';

      // Convert line breaks in database content to HTML
      const processedText = convertLineBreaks(page.text);
      textContent.innerHTML = `<p>${processedText}</p>`;

      textDiv.appendChild(textContent);
      pageBody.appendChild(textDiv);
    }
  } else {
    // Overlay mode: use absolute positioning (existing code)

    // Full page mode: set image as background on page-content, not inside page-body
    if (imageSize === 'full_page' && page.imageUrl && page.imageUrl !== '') {
      pageContent.style.backgroundImage = `url('${page.imageUrl}')`;
      pageContent.style.backgroundSize = 'cover';
      pageContent.style.backgroundPosition = 'center';
      pageContent.style.backgroundRepeat = 'no-repeat';
      pageContent.classList.add('page-content--full-page-bg');
    } else if (page.imageUrl && page.imageUrl !== '') {
      // Other image modes: create image layer inside page-body
      const imageContainer = document.createElement('div');
      imageContainer.className = `page-image page-image--${imageSize}`;

      const img = document.createElement('img');
      img.src = page.imageUrl;
      img.alt = 'Story illustration';

      img.onerror = function () {
        console.error('Failed to load image:', this.src);
        this.style.display = 'none';
      };

      img.onload = function () {
        console.log('Image loaded successfully:', this.src);
      };

      imageContainer.appendChild(img);
      pageBody.appendChild(imageContainer);
    }

    // Create text layer (only if there's actual text content)
    if (page.text && page.text.trim() !== '') {
      const textDiv = document.createElement('div');
      textDiv.className = `page-text page-text--${textPosition}`;
      textDiv.style.textAlign = textAlign;
      textDiv.dataset.textSize = textSize;

      // Create text content wrapper
      const textContent = document.createElement('div');
      textContent.className = 'text-content';

      // Convert line breaks in database content to HTML
      const processedText = convertLineBreaks(page.text);
      textContent.innerHTML = `<p>${processedText}</p>`;

      textDiv.appendChild(textContent);
      pageBody.appendChild(textDiv);
    }
  }

  pageContent.appendChild(pageBody);

  // Add page footer with "X / Y" format (language neutral)
  const footer = document.createElement('div');
  footer.className = 'page-footer';
  // Footer uses fixed size — not tied to page text size for consistency across all pages
  footer.innerHTML = `<span class="page-number">${currentPageNumber} / ${totalPages}</span>`;
  pageContent.appendChild(footer);

  pageElement.appendChild(pageContent);

  return pageElement;
}

/**
 * Navigate to next page
 * @param {Object} pageFlip - PageFlip instance
 */
export function nextPage(pageFlip) {
  if (pageFlip) {
    pageFlip.flipNext();
  }
}

/**
 * Navigate to previous page
 * @param {Object} pageFlip - PageFlip instance
 */
export function prevPage(pageFlip) {
  if (pageFlip) {
    pageFlip.flipPrev();
  }
}

/**
 * Go to specific page
 * @param {Object} pageFlip - PageFlip instance
 * @param {number} pageIndex - Page index to navigate to
 */
export function goToPage(pageFlip, pageIndex) {
  if (pageFlip) {
    pageFlip.flip(pageIndex);
  }
}

/**
 * Get current page index
 * @param {Object} pageFlip - PageFlip instance
 * @returns {number} Current page index
 */
export function getCurrentPage(pageFlip) {
  return pageFlip ? pageFlip.getCurrentPageIndex() : 0;
}

/**
 * Get total page count
 * @param {Object} pageFlip - PageFlip instance
 * @returns {number} Total number of pages
 */
export function getPageCount(pageFlip) {
  return pageFlip ? pageFlip.getPageCount() : 0;
}

/**
 * Destroy page flip instance
 * @param {Object} pageFlip - PageFlip instance
 */
export function destroyPageFlip(pageFlip) {
  if (pageFlip) {
    pageFlip.destroy();
  }
}

/**
 * Calculate appropriate text size based on device, orientation, and text size setting
 *
 * This function determines the correct font size for story page text by:
 * 1. Detecting the current device and orientation
 * 2. Checking if the browser supports container queries (cqi units)
 * 3. Returning either container query value or fallback pixel value
 *
 * Container query units (cqi) scale proportionally to the page container width,
 * which changes when StPageFlip switches between single and double page modes.
 *
 * @param {string} textSize - Text size setting: 'small', 'medium', or 'large'
 * @param {HTMLElement} pageElement - The page element (for fallback calculation if needed)
 * @returns {string} CSS font-size value (e.g., '4cqi + 1rem' or '16px')
 *
 * @example
 * // Returns container query value if supported
 * const fontSize = calculateTextSize('medium');
 * // Returns: '4cqi + 1rem' (on supported browsers)
 * // Returns: '16.48px' (on older browsers, for Pixel 7 portrait)
 */
export function calculateTextSize(textSize = 'medium', pageElement = null) {
  // eslint-disable-line no-unused-vars
  // Validate text size parameter
  const validSizes = ['small', 'medium', 'large'];
  if (!validSizes.includes(textSize)) {
    console.warn(`⚠️ Invalid text size "${textSize}", defaulting to "medium"`);
    textSize = 'medium';
  }

  // Detect device and orientation
  const { deviceId, orientation } = detectDeviceAndOrientation();

  // Check container query support
  const useContainerQueries = supportsContainerQueries();

  // Get text size value from configuration
  const fontSize = getTextSizeValue(deviceId, orientation, textSize, useContainerQueries);

  // Fallback for unknown devices
  if (!fontSize) {
    console.warn(`⚠️ No text sizing config for device "${deviceId}", using default`);

    // Default fallback values
    const defaultSizes = {
      small: useContainerQueries ? '3cqi + 0.5rem' : '14px',
      medium: useContainerQueries ? '4cqi + 1rem' : '18px',
      large: useContainerQueries ? '5cqi + 1rem' : '22px',
    };

    return defaultSizes[textSize];
  }

  // Log for debugging
  console.log(`📏 Text sizing:`, {
    deviceId,
    orientation,
    textSize,
    fontSize,
    containerQueries: useContainerQueries,
  });

  return fontSize;
}

/**
 * Apply text sizing to a page element
 *
 * This function applies the calculated text size to the text container
 * within a page element. It handles both container query and fallback modes.
 *
 * @param {HTMLElement} pageElement - The page element containing text
 * @param {string} textSize - Text size setting: 'small', 'medium', or 'large'
 *
 * @example
 * const pageElement = document.querySelector('.page');
 * applyTextSizeToPage(pageElement, 'large');
 */
export function applyTextSizeToPage(pageElement, textSize = 'medium') {
  if (!pageElement) {
    console.warn('⚠️ No page element provided to applyTextSizeToPage');
    return;
  }

  // Find text container within page
  const textContainer = pageElement.querySelector('.page-text');
  if (!textContainer) {
    console.warn('⚠️ No .page-text element found in page');
    return;
  }

  // Calculate font size
  const fontSize = calculateTextSize(textSize, pageElement);

  // Apply font size
  textContainer.style.fontSize = fontSize;

  // Store text size as data attribute for debugging
  pageElement.dataset.textSize = textSize;

  console.log(`✅ Applied text size "${textSize}" (${fontSize}) to page`);
}

/**
 * Apply text sizing to all pages
 *
 * This function applies text sizing to all page elements in the document.
 * Useful for initial setup or when text size preference changes.
 *
 * @param {string} textSize - Text size setting: 'small', 'medium', or 'large'
 *
 * @example
 * // Apply medium text size to all pages
 * applyTextSizeToAllPages('medium');
 */
export function applyTextSizeToAllPages(textSize = 'medium') {
  const pageElements = document.querySelectorAll('.page');

  if (pageElements.length === 0) {
    console.warn('⚠️ No page elements found');
    return;
  }

  console.log(`📚 Applying text size "${textSize}" to ${pageElements.length} pages`);

  pageElements.forEach((pageElement) => {
    applyTextSizeToPage(pageElement, textSize);
  });
}

/**
 * Update text sizing on orientation change or resize
 *
 * This function recalculates and applies text sizing when the device
 * orientation changes or the window is resized. It's designed to be
 * called from event handlers.
 *
 * @param {string} textSize - Text size setting: 'small', 'medium', or 'large'
 *
 * @example
 * window.addEventListener('orientationchange', () => {
 *   updateTextSizingOnResize('medium');
 * });
 */
export function updateTextSizingOnResize(textSize = 'medium') {
  console.log('🔄 Updating text sizing after orientation/resize');
  applyTextSizeToAllPages(textSize);
}

/**
 * Apply layout configuration to a page element
 *
 * This function applies all layout properties to a page element:
 * - Image size mode (6 options: full_page, fit_width, fit_height, center, top, bottom)
 * - Text alignment (3 options: left, center, right)
 * - Text position (3 options: top, middle, bottom)
 * - Text size (3 options: small, medium, large)
 *
 * The function handles the special case of full_page image mode by creating
 * an overlay layout where text appears on top of the image with a semi-transparent
 * background for readability.
 *
 * @param {HTMLElement} pageElement - The page container element (.page)
 * @param {Object} layoutConfig - Layout configuration object
 * @param {string} layoutConfig.imageSize - Image size mode: 'full_page' | 'fit_width' | 'fit_height' | 'center' | 'top' | 'bottom'
 * @param {string} layoutConfig.textAlign - Text alignment: 'left' | 'center' | 'right'
 * @param {string} layoutConfig.textPosition - Text position: 'top' | 'middle' | 'bottom'
 * @param {string} layoutConfig.textSize - Text size: 'small' | 'medium' | 'large'
 *
 * @example
 * const layoutConfig = {
 *   imageSize: 'center',
 *   textAlign: 'left',
 *   textPosition: 'bottom',
 *   textSize: 'medium'
 * };
 * applyPageLayout(pageElement, layoutConfig);
 */
export function applyPageLayout(pageElement, layoutConfig) {
  if (!pageElement) {
    console.warn('⚠️ No page element provided to applyPageLayout');
    return;
  }

  // Extract layout properties with defaults
  const {
    imageSize = 'center',
    textAlign = 'left',
    textPosition = 'bottom',
    textSize = 'medium',
  } = layoutConfig || {};

  // Validate layout properties
  const validImageSizes = ['full_page', 'fit_width', 'fit_height', 'center', 'top', 'bottom'];
  const validTextAligns = ['left', 'center', 'right'];
  const validTextPositions = ['top', 'middle', 'bottom'];

  if (!validImageSizes.includes(imageSize)) {
    console.warn(`⚠️ Invalid imageSize "${imageSize}", defaulting to "center"`);
  }
  if (!validTextAligns.includes(textAlign)) {
    console.warn(`⚠️ Invalid textAlign "${textAlign}", defaulting to "left"`);
  }
  if (!validTextPositions.includes(textPosition)) {
    console.warn(`⚠️ Invalid textPosition "${textPosition}", defaulting to "bottom"`);
  }

  // Get child elements
  const imageContainer = pageElement.querySelector('.page-image');
  const textContainer = pageElement.querySelector('.page-text');
  const pageBody = pageElement.querySelector('.page-body');

  // Apply image size mode
  if (imageContainer) {
    // Remove any existing image size classes
    imageContainer.className = imageContainer.className
      .split(' ')
      .filter((cls) => !cls.startsWith('page-image--'))
      .join(' ');

    // Add new image size class
    imageContainer.classList.add(`page-image--${imageSize}`);
    imageContainer.dataset.imageSize = imageSize;

    console.log(`🖼️ Applied image size: ${imageSize}`);
  } else {
    console.log('ℹ️ No image container found (text-only page)');
  }

  // Apply text alignment
  if (textContainer) {
    textContainer.style.textAlign = textAlign;
    textContainer.dataset.textAlign = textAlign;

    console.log(`📝 Applied text align: ${textAlign}`);
  } else {
    console.warn('⚠️ No text container found in page');
  }

  // Apply text vertical position
  if (textContainer) {
    // Remove any existing text position classes
    textContainer.className = textContainer.className
      .split(' ')
      .filter((cls) => !cls.startsWith('page-text--'))
      .join(' ');

    // Add new text position class
    textContainer.classList.add(`page-text--${textPosition}`);
    textContainer.dataset.textPosition = textPosition;

    console.log(`📍 Applied text position: ${textPosition}`);
  }

  // Apply text size
  if (textContainer) {
    applyTextSizeToPage(pageElement, textSize);
  }

  // Configure page body layout based on image size
  if (pageBody) {
    if (imageSize === 'full_page') {
      // Full page image mode: overlay layout
      pageBody.style.position = 'relative';
      pageBody.style.display = 'block';

      // Image fills entire page
      if (imageContainer) {
        imageContainer.style.position = 'absolute';
        imageContainer.style.top = '0';
        imageContainer.style.left = '0';
        imageContainer.style.width = '100%';
        imageContainer.style.height = '100%';
        imageContainer.style.zIndex = '1';
      }

      // Text overlays image with semi-transparent background
      if (textContainer) {
        textContainer.style.position = 'relative';
        textContainer.style.zIndex = '10';
        textContainer.style.background = 'rgba(255, 255, 255, 0.9)';
        textContainer.style.padding = '1rem';
        textContainer.style.borderRadius = '0.5rem';
        textContainer.style.backdropFilter = 'blur(8px)';
        textContainer.style.webkitBackdropFilter = 'blur(8px)';
      }

      console.log('🎨 Applied full_page overlay layout');
    } else {
      // Standard layout: flex column
      pageBody.style.display = 'flex';
      pageBody.style.flexDirection = 'column';
      pageBody.style.position = 'static';

      // Reset any overlay styles
      if (imageContainer) {
        imageContainer.style.position = '';
        imageContainer.style.top = '';
        imageContainer.style.left = '';
        imageContainer.style.width = '';
        imageContainer.style.height = '';
        imageContainer.style.zIndex = '';
      }

      if (textContainer) {
        textContainer.style.position = '';
        textContainer.style.zIndex = '';
        textContainer.style.background = '';
        textContainer.style.padding = '';
        textContainer.style.borderRadius = '';
        textContainer.style.backdropFilter = '';
        textContainer.style.webkitBackdropFilter = '';
      }

      console.log('📐 Applied standard flex layout');
    }
  } else {
    console.warn('⚠️ No page body found in page');
  }

  // Store layout config as data attributes for debugging
  pageElement.dataset.imageSize = imageSize;
  pageElement.dataset.textAlign = textAlign;
  pageElement.dataset.textPosition = textPosition;
  pageElement.dataset.textSize = textSize;

  console.log(
    `✅ Applied layout: image=${imageSize}, text=${textAlign}/${textPosition}/${textSize}`
  );
}

/**
 * Apply layout configuration to all pages
 *
 * This function applies the same layout configuration to all page elements
 * in the document. Useful when applying a global layout preference.
 *
 * @param {Object} layoutConfig - Layout configuration object
 *
 * @example
 * const layoutConfig = {
 *   imageSize: 'fit_width',
 *   textAlign: 'center',
 *   textPosition: 'bottom',
 *   textSize: 'large'
 * };
 * applyLayoutToAllPages(layoutConfig);
 */
export function applyLayoutToAllPages(layoutConfig) {
  const pageElements = document.querySelectorAll('.page');

  if (pageElements.length === 0) {
    console.warn('⚠️ No page elements found');
    return;
  }

  console.log(`📚 Applying layout to ${pageElements.length} pages`);

  pageElements.forEach((pageElement) => {
    applyPageLayout(pageElement, layoutConfig);
  });
}

/**
 * Initialize page layouts for all pages in a story
 *
 * This function should be called after pages are created and added to the DOM,
 * but before StPageFlip is initialized. It applies layout configuration to
 * each page based on the page data.
 *
 * Supports both new layout properties and legacy layout values for backward compatibility.
 *
 * @param {Array} pages - Array of page data objects with layout properties
 * @param {HTMLElement} container - Container element containing page elements
 *
 * @example
 * // After creating page elements
 * const pageElements = createPageElements(pages, storyTitle);
 * pageElements.forEach(el => container.appendChild(el));
 *
 * // Initialize layouts before StPageFlip
 * initializePageLayouts(pages, container);
 *
 * // Then initialize StPageFlip
 * const pageFlip = initializePageFlip(container, pages, callbacks);
 */
export function initializePageLayouts(pages, container) {
  if (!container) {
    console.warn('⚠️ No container provided to initializePageLayouts');
    return;
  }

  const pageElements = container.querySelectorAll('.page');

  if (pageElements.length === 0) {
    console.warn('⚠️ No page elements found in container');
    return;
  }

  if (pages.length !== pageElements.length) {
    console.warn(
      `⚠️ Page count mismatch: ${pages.length} data objects vs ${pageElements.length} DOM elements`
    );
  }

  console.log(`🎨 Initializing layouts for ${pageElements.length} pages`);

  // Apply layout to each page
  pageElements.forEach((pageElement, index) => {
    const pageData = pages[index];

    if (!pageData) {
      console.warn(`⚠️ No page data for page ${index}`);
      return;
    }

    // Get layout config with backward compatibility
    const layoutConfig = getLayoutConfigFromPageData(pageData);

    // Apply layout to this page
    applyPageLayout(pageElement, layoutConfig);
  });

  console.log('✅ Page layouts initialized');
}

/**
 * Update text sizing for all pages
 *
 * This function recalculates and applies text sizing to all pages.
 * It should be called when:
 * - Device orientation changes
 * - Window is resized
 * - StPageFlip changes between single/double page mode
 *
 * The function preserves each page's individual text size setting
 * (small/medium/large) while recalculating the actual pixel/cqi values
 * based on the new device state.
 *
 * @example
 * // Call on orientation change
 * window.addEventListener('orientationchange', updateTextSizing);
 *
 * // Call on StPageFlip orientation change
 * pageFlip.on('changeOrientation', updateTextSizing);
 */
export function updateTextSizing() {
  const pageElements = document.querySelectorAll('.page');

  if (pageElements.length === 0) {
    console.warn('⚠️ No page elements found');
    return;
  }

  console.log(`🔄 Updating text sizing for ${pageElements.length} pages`);

  pageElements.forEach((pageElement) => {
    // Get the text size setting from data attribute
    const textSize = pageElement.dataset.textSize || 'medium';

    // Reapply text sizing with current device state
    applyTextSizeToPage(pageElement, textSize);
  });

  console.log('✅ Text sizing updated');
}

// Debounce helper for resize events
let resizeTimeout = null;

/**
 * Handle window resize events with debouncing
 *
 * This function is called on window resize and debounces the text sizing
 * update to avoid excessive recalculations during continuous resize events.
 *
 * @private
 */
function handleResize() {
  // Clear existing timeout
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  // Set new timeout
  resizeTimeout = setTimeout(() => {
    console.log('📐 Window resized, updating text sizing');
    updateTextSizing();
  }, 250); // 250ms debounce delay
}

/**
 * Setup event listeners for layout updates
 *
 * This function sets up all necessary event listeners for updating
 * page layouts when device state changes. It should be called once
 * after StPageFlip is initialized.
 *
 * @param {Object} pageFlip - StPageFlip instance
 *
 * @example
 * const pageFlip = initializePageFlip(container, pages, callbacks);
 * setupLayoutEventListeners(pageFlip);
 */
export function setupLayoutEventListeners(pageFlip) {
  // Listen for orientation changes
  window.addEventListener('orientationchange', () => {
    console.log('📱 Orientation changed');
    // Small delay to ensure layout has updated
    setTimeout(updateTextSizing, 100);
  });

  // Listen for window resize (debounced)
  window.addEventListener('resize', handleResize);

  // Listen for StPageFlip orientation changes
  if (pageFlip) {
    pageFlip.on('changeOrientation', (e) => {
      console.log('📖 PageFlip orientation changed:', e);
      updateTextSizing();
    });

    // Listen for page flip events (optional - for debugging)
    pageFlip.on('flip', (e) => {
      console.log('📄 Page flipped:', e);
      // Text sizing doesn't need to update on flip, but we log it for debugging
    });
  }

  console.log('✅ Layout event listeners setup complete');
}

/**
 * Cleanup event listeners
 *
 * This function removes all event listeners set up by setupLayoutEventListeners.
 * Call this when destroying the page flip instance or navigating away from the reader.
 *
 * @example
 * // When leaving the reader
 * cleanupLayoutEventListeners();
 * destroyPageFlip(pageFlip);
 */
export function cleanupLayoutEventListeners() {
  window.removeEventListener('orientationchange', updateTextSizing);
  window.removeEventListener('resize', handleResize);

  // Clear any pending resize timeout
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
    resizeTimeout = null;
  }

  console.log('✅ Layout event listeners cleaned up');
}

/**
 * Get layout configuration from legacy layout value
 *
 * This function maps legacy layout values ('standard', 'text-top', 'text-bottom')
 * to the new layout configuration format. This ensures backward compatibility
 * with existing stories that use the old layout system.
 *
 * Legacy layout values:
 * - 'standard' or 'text-bottom': Image above text, text at bottom
 * - 'text-top': Text above image, text at top
 *
 * @param {string} legacyLayout - Legacy layout value: 'standard', 'text-top', or 'text-bottom'
 * @returns {Object} Layout configuration object
 *
 * @example
 * const layoutConfig = getLegacyLayoutConfig('text-top');
 * // Returns: { imageSize: 'center', textAlign: 'left', textPosition: 'top', textSize: 'medium' }
 */
export function getLegacyLayoutConfig(legacyLayout) {
  // Default configuration (matches 'standard' and 'text-bottom')
  const defaultConfig = {
    imageSize: 'center',
    textAlign: 'left',
    textPosition: 'bottom',
    textSize: 'medium',
  };

  // Handle null/undefined
  if (!legacyLayout) {
    console.log('ℹ️ No legacy layout specified, using default config');
    return defaultConfig;
  }

  // Normalize legacy layout value
  const normalizedLayout = legacyLayout.toLowerCase().trim();

  // Map legacy values to new configuration
  switch (normalizedLayout) {
    case 'text-top':
      console.log('🔄 Mapping legacy layout "text-top" to new config');
      return {
        imageSize: 'center',
        textAlign: 'left',
        textPosition: 'top',
        textSize: 'medium',
      };

    case 'text-bottom':
    case 'standard':
    default:
      console.log(`🔄 Mapping legacy layout "${normalizedLayout}" to default config`);
      return defaultConfig;
  }
}

/**
 * Get layout configuration from page data with backward compatibility
 *
 * This function extracts layout configuration from page data, handling both
 * new layout properties and legacy layout values. It checks for new properties
 * first, then falls back to legacy layout mapping if needed.
 *
 * Priority order:
 * 1. New layout properties (image_size, text_align, text_position, text_size)
 * 2. Legacy layout value (layout: 'standard', 'text-top', 'text-bottom')
 * 3. Default configuration
 *
 * @param {Object} pageData - Page data object from API
 * @returns {Object} Layout configuration object
 *
 * @example
 * // New format page
 * const pageData = { image_size: 'fit_width', text_align: 'center', ... };
 * const config = getLayoutConfigFromPageData(pageData);
 *
 * // Legacy format page
 * const legacyPage = { layout: 'text-top', ... };
 * const config = getLayoutConfigFromPageData(legacyPage);
 */
export function getLayoutConfigFromPageData(pageData) {
  if (!pageData) {
    console.warn('⚠️ No page data provided, using default config');
    return getLegacyLayoutConfig(null);
  }

  // Check if page has new layout properties
  const hasNewProperties =
    pageData.image_size ||
    pageData.imageSize ||
    pageData.text_align ||
    pageData.textAlign ||
    pageData.text_position ||
    pageData.textPosition ||
    pageData.text_size ||
    pageData.textSize;

  if (hasNewProperties) {
    // Use new layout properties
    const config = {
      imageSize: pageData.image_size || pageData.imageSize || 'center',
      textAlign: pageData.text_align || pageData.textAlign || 'left',
      textPosition: pageData.text_position || pageData.textPosition || 'bottom',
      textSize: pageData.text_size || pageData.textSize || 'medium',
    };

    console.log('✅ Using new layout properties:', config);
    return config;
  }

  // Fall back to legacy layout mapping
  const legacyLayout = pageData.layout;
  console.log(`🔄 No new layout properties found, using legacy layout: "${legacyLayout}"`);
  return getLegacyLayoutConfig(legacyLayout);
}
